import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail, fileUrl } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, FileText, Trash2, Pencil, Download, Upload, Eye } from "lucide-react";
import { toast } from "sonner";

const emptyForm = () => ({ name: "", project_id: "ALL", mandatory: false, form_type: "upload", file_base64: "", file_name: "", items: [""], signature_roles: ["Inspector", "Contractor (WABTEC)", "Contractor (The Retrofitter)"] });

const readFile = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

export default function Forms() {
  const { t } = useTranslation();
  const [forms, setForms] = useState(null);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);
  const [delForm, setDelForm] = useState(null);
  const [preview, setPreview] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const [f, p] = await Promise.all([api.get("/forms"), api.get("/projects")]);
    setForms(f.data);
    setProjects(p.data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setPreview(false); setOpen(true); };
  const openEdit = (fm) => {
    setEditing(fm);
    setForm({
      name: fm.name, project_id: fm.project_id, mandatory: fm.mandatory,
      form_type: fm.form_type || "upload", file_base64: "", file_name: fm.file_name,
      items: (fm.items || []).map((i) => i.text).length ? (fm.items || []).map((i) => i.text) : [""],
      signature_roles: (fm.signature_roles || []).length ? fm.signature_roles : ["Inspector"],
    });
    setPreview(false);
    setOpen(true);
  };

  const setItem = (i, v) => setForm((s) => ({ ...s, items: s.items.map((x, idx) => idx === i ? v : x) }));
  const addItem = () => setForm((s) => ({ ...s, items: [...s.items, ""] }));
  const removeItem = (i) => setForm((s) => ({ ...s, items: s.items.length > 1 ? s.items.filter((_, idx) => idx !== i) : s.items }));
  const setSigRole = (i, v) => setForm((s) => ({ ...s, signature_roles: s.signature_roles.map((x, idx) => idx === i ? v : x) }));
  const addSigRole = () => setForm((s) => ({ ...s, signature_roles: [...s.signature_roles, ""] }));
  const removeSigRole = (i) => setForm((s) => ({ ...s, signature_roles: s.signature_roles.filter((_, idx) => idx !== i) }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const dataUrl = await readFile(file);
    setForm((s) => ({ ...s, file_base64: dataUrl, file_name: file.name }));
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error(t("forms.nameRequired"));
    const isChecklist = form.form_type === "checklist";
    if (!isChecklist && !editing && !form.file_base64) return toast.error(t("forms.fileRequired"));
    if (isChecklist && !form.items.some((i) => i.trim())) return toast.error(t("forms.itemsRequired"));
    setBusy(true);
    try {
      const base = { name: form.name, project_id: form.project_id, mandatory: form.mandatory, form_type: form.form_type };
      if (isChecklist) { base.items = form.items.filter((i) => i.trim()); base.signature_roles = form.signature_roles.filter((s) => s.trim()); }
      if (form.file_base64) { base.file_base64 = form.file_base64; base.file_name = form.file_name; }
      if (editing) {
        await api.patch(`/forms/${editing.id}`, base);
        toast.success(t("forms.updated"));
      } else {
        await api.post("/forms", base);
        toast.success(t("forms.created"));
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try { await api.delete(`/forms/${delForm.id}`); toast.success(t("forms.deleted")); setDelForm(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!forms) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6" data-testid="forms-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-black text-slate-900">{t("forms.title")}</h1>
          <p className="mt-1 text-slate-500">{t("forms.subtitle")}</p>
        </div>
        <Button data-testid="add-form-btn" onClick={openCreate} className="gap-2 bg-blue-600 hover:bg-blue-500"><Plus className="h-4 w-4" /> {t("forms.addForm")}</Button>
      </div>

      <div className="grid gap-3">
        {forms.length === 0 && (
          <div className="rounded-md border border-dashed border-slate-300 py-12 text-center text-slate-400" data-testid="no-forms">{t("forms.empty")}</div>
        )}
        {forms.map((fm) => (
          <Card key={fm.id} data-testid={`form-row-${fm.id}`} className="flex flex-wrap items-center gap-3 border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-blue-50 text-blue-600"><FileText className="h-5 w-5" /></div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-heading font-bold text-slate-900">{fm.name}</span>
                {fm.mandatory
                  ? <Badge className="border-0 bg-red-100 text-red-700">{t("forms.mandatory")}</Badge>
                  : <Badge className="border-0 bg-slate-100 text-slate-600">{t("forms.optional")}</Badge>}
                {fm.form_type === "checklist" && <Badge className="border-0 bg-indigo-100 text-indigo-700">{t("forms.typeChecklist")}</Badge>}
              </div>
              <div className="mt-0.5 text-sm text-slate-500">{fm.project_name}{fm.form_type === "checklist" ? ` · ${(fm.items || []).length} ${t("forms.itemsCount")}` : ` · ${fm.file_name || ""}`}</div>
            </div>
            {fm.form_type === "upload" && (
              <a href={fileUrl(fm.file_path)} target="_blank" rel="noreferrer" data-testid={`download-form-${fm.id}`}>
                <Button size="sm" variant="ghost" className="gap-1.5 text-slate-600"><Download className="h-4 w-4" /> {t("forms.template")}</Button>
              </a>
            )}
            <Button size="sm" variant="ghost" data-testid={`edit-form-${fm.id}`} onClick={() => openEdit(fm)} className="gap-1.5 text-slate-600"><Pencil className="h-4 w-4" /> {t("forms.edit")}</Button>
            <Button size="sm" variant="ghost" data-testid={`delete-form-${fm.id}`} onClick={() => setDelForm(fm)} className="gap-1.5 text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /> {t("forms.delete")}</Button>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto border-slate-200 bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-slate-900">{editing ? t("forms.editTitle") : t("forms.addForm")}</DialogTitle>
            <DialogDescription>{t("forms.subtitle")}</DialogDescription>
          </DialogHeader>
          {preview && form.form_type === "checklist" ? (
            <div className="space-y-4 rounded-md border border-slate-200 bg-slate-50 p-4" data-testid="checklist-preview">
              <div className="text-center">
                <h3 className="font-heading text-lg font-bold text-slate-900">{form.name || t("forms.namePlaceholder")}</h3>
                <p className="text-xs text-slate-400">{t("forms.previewHint")}</p>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="rounded border border-slate-200 bg-white p-2"><div className="overline text-slate-400">{t("checklist.contractor")}</div><div className="h-4" /></div>
                <div className="rounded border border-slate-200 bg-white p-2"><div className="overline text-slate-400">{t("checklist.agency")}</div><div className="h-4" /></div>
                <div className="rounded border border-slate-200 bg-white p-2"><div className="overline text-slate-400">{t("checklist.date")}</div><div className="h-4" /></div>
              </div>
              <div className="space-y-2">
                {form.items.filter((i) => i.trim()).map((it, i) => (
                  <div key={i} className="rounded-md border border-slate-200 bg-white p-2.5">
                    <div className="text-sm font-medium text-slate-800"><span className="text-slate-400">{i + 1}.</span> {it}</div>
                    <div className="mt-1.5 flex gap-1.5">
                      {["Pass", "Fail", "N/A"].map((r) => <span key={r} className="rounded-full border border-slate-300 px-2.5 py-0.5 text-xs text-slate-500">{r}</span>)}
                      <span className="ml-2 flex-1 rounded border border-dashed border-slate-300 px-2 py-0.5 text-xs text-slate-300">{t("checklist.commentPlaceholder")}</span>
                    </div>
                  </div>
                ))}
                {form.items.filter((i) => i.trim()).length === 0 && <p className="text-sm text-slate-400">{t("forms.itemsRequired")}</p>}
              </div>
              <div>
                <div className="overline text-slate-500">{t("checklist.deficiencies")}</div>
                <div className="mt-1 h-12 rounded border border-dashed border-slate-300 bg-white" />
              </div>
              <div>
                <div className="overline mb-1.5 text-slate-500">{t("checklist.signoff")}</div>
                <div className="grid grid-cols-2 gap-3">
                  {form.signature_roles.filter((s) => s.trim()).map((sr, i) => (
                    <div key={i} className="rounded-md border border-slate-200 bg-white p-2">
                      <div className="text-xs font-medium text-slate-700">{sr}</div>
                      <div className="mt-1 h-12 rounded border border-dashed border-slate-300" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
          <div className="space-y-3">
            <div>
              <Label className="overline text-slate-500">{t("forms.formType")}</Label>
              <Select value={form.form_type} onValueChange={(v) => setForm({ ...form, form_type: v })}>
                <SelectTrigger data-testid="form-type" className="mt-1.5 border-slate-300 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="upload" data-testid="form-type-upload">{t("forms.typeUpload")}</SelectItem>
                  <SelectItem value="checklist" data-testid="form-type-checklist">{t("forms.typeChecklist")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline text-slate-500">{t("forms.name")}</Label>
              <Input data-testid="form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("forms.namePlaceholder")} className="mt-1.5 border-slate-300 bg-white" />
            </div>
            <div>
              <Label className="overline text-slate-500">{t("forms.project")}</Label>
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="form-project" className="mt-1.5 border-slate-300 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">{t("forms.allProjects")}</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name || p.id}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="form-mandatory" data-testid="form-mandatory" checked={form.mandatory} onCheckedChange={(v) => setForm({ ...form, mandatory: !!v })} />
              <Label htmlFor="form-mandatory" className="cursor-pointer text-sm text-slate-700">{t("forms.mandatoryLabel")}</Label>
            </div>

            {form.form_type === "upload" ? (
              <div>
                <Label className="overline text-slate-500">{t("forms.file")}</Label>
                <input ref={fileRef} type="file" accept=".pdf,image/*" onChange={onFile} className="hidden" data-testid="form-file-input" />
                <div className="mt-1.5 flex items-center gap-2">
                  <Button type="button" variant="outline" data-testid="form-file-btn" onClick={() => fileRef.current?.click()} className="gap-2 border-slate-300"><Upload className="h-4 w-4" /> {t("forms.chooseFile")}</Button>
                  <span className="truncate text-sm text-slate-500">{form.file_name || t("forms.noFile")}</span>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <Label className="overline text-slate-500">{t("forms.items")}</Label>
                  <div className="mt-1.5 space-y-2">
                    {form.items.map((it, i) => (
                      <div key={i} className="flex items-center gap-2" data-testid={`form-item-${i}`}>
                        <span className="w-5 text-right text-xs text-slate-400">{i + 1}.</span>
                        <Input data-testid={`form-item-input-${i}`} value={it} onChange={(e) => setItem(i, e.target.value)} placeholder={t("forms.itemPlaceholder")} className="border-slate-300 bg-white" />
                        <Button type="button" size="icon" variant="ghost" data-testid={`remove-item-${i}`} onClick={() => removeItem(i)} disabled={form.items.length === 1} className="shrink-0 text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    ))}
                  </div>
                  <Button type="button" size="sm" variant="outline" data-testid="add-item" onClick={addItem} className="mt-2 gap-1.5 border-slate-300"><Plus className="h-4 w-4" /> {t("forms.addItem")}</Button>
                </div>
                <div>
                  <Label className="overline text-slate-500">{t("forms.signatureBlocks")}</Label>
                  <div className="mt-1.5 space-y-2">
                    {form.signature_roles.map((sr, i) => (
                      <div key={i} className="flex items-center gap-2" data-testid={`form-sig-${i}`}>
                        <Input data-testid={`form-sig-input-${i}`} value={sr} onChange={(e) => setSigRole(i, e.target.value)} placeholder={t("forms.signaturePlaceholder")} className="border-slate-300 bg-white" />
                        <Button type="button" size="icon" variant="ghost" data-testid={`remove-sig-${i}`} onClick={() => removeSigRole(i)} className="shrink-0 text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    ))}
                  </div>
                  <Button type="button" size="sm" variant="outline" data-testid="add-sig" onClick={addSigRole} className="mt-2 gap-1.5 border-slate-300"><Plus className="h-4 w-4" /> {t("forms.addSignature")}</Button>
                </div>
              </>
            )}
          </div>
          )}
          <DialogFooter>
            {form.form_type === "checklist" && (
              <Button type="button" variant="outline" data-testid="toggle-preview" onClick={() => setPreview((p) => !p)} className="mr-auto gap-1.5 border-slate-300">
                <Eye className="h-4 w-4" /> {preview ? t("forms.backToEdit") : t("forms.preview")}
              </Button>
            )}
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-300">{t("forms.cancel")}</Button>
            <Button data-testid="save-form" onClick={save} disabled={busy} className="gap-1.5 bg-blue-600 hover:bg-blue-500">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("forms.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delForm} onOpenChange={() => setDelForm(null)}>
        <DialogContent className="border-slate-200 bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-slate-900">{t("forms.deleteTitle")}</DialogTitle>
            <DialogDescription>{t("forms.deleteConfirm", { name: delForm?.name })}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelForm(null)} className="border-slate-300">{t("forms.cancel")}</Button>
            <Button data-testid="confirm-delete-form" onClick={remove} disabled={busy} className="gap-1.5 bg-red-600 hover:bg-red-500">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} {t("forms.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
